/**
 * Emulator Page - Main emulator interface
 * Using melonDS WASM for Nintendo DS emulation with cloud save sync
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { calculateROMHash } from '../utils/romUtils';
import { EmulatorCore } from '../emulator/EmulatorCore';
import { RenderEngine } from '../emulator/RenderEngine';
import { AudioEngine } from '../emulator/AudioEngine';
import { InputManager, loadKeyMapping } from '../emulator/InputManager';
import { SaveSyncManager } from '../services/SaveSyncManager';
import KeyBindingsSettings from '../components/KeyBindingsSettings';
import toast from 'react-hot-toast';
import '../styles/emulator.css';

export default function Emulator() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Canvas refs
  const topCanvasRef = useRef<HTMLCanvasElement>(null);
  const bottomCanvasRef = useRef<HTMLCanvasElement>(null);

  // Emulator systems
  const [emulatorCore] = useState(() => new EmulatorCore());
  const [renderEngine, setRenderEngine] = useState<RenderEngine | null>(null);
  const [audioEngine] = useState(() => new AudioEngine());
  const [inputManager, setInputManager] = useState<InputManager | null>(null);
  const [saveSyncManager, setSaveSyncManager] = useState<SaveSyncManager | null>(null);

  // State
  const [isLoading, setIsLoading] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [fps, setFps] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [romInfo, setRomInfo] = useState<{ hash: string; name: string; size: number } | null>(null);
  const [showKeyBindings, setShowKeyBindings] = useState(false);
  const [lastSaveTime, setLastSaveTime] = useState<Date | null>(null);

  // Animation frame
  const animationFrameRef = useRef<number | null>(null);
  const frameCountRef = useRef<number>(0);
  const fpsUpdateTimeRef = useRef<number>(0);

  /**
   * Initialize emulator systems
   */
  useEffect(() => {
    const init = async () => {
      try {
        setIsLoading(true);

        console.log('🎮 Initializing emulator systems...');

        // Initialize core systems
        await emulatorCore.initialize();
        await audioEngine.initialize();

        // Initialize renderer
        if (topCanvasRef.current && bottomCanvasRef.current) {
          const renderer = new RenderEngine({
            topCanvas: topCanvasRef.current,
            bottomCanvas: bottomCanvasRef.current,
            scale: 2,
            smoothing: false,
          });
          setRenderEngine(renderer);
          renderer.drawPlaceholder('Upload a ROM file to begin');
        }

        // Initialize input manager with custom key bindings
        const keyMapping = loadKeyMapping();
        const inputMgr = new InputManager(keyMapping);
        inputMgr.initialize((button, pressed) => {
          emulatorCore.setButtonState(button, pressed);
        });
        setInputManager(inputMgr);

        console.log('✅ All emulator systems initialized');
      } catch (err) {
        console.error('❌ Emulator initialization failed:', err);
        setError(err instanceof Error ? err.message : 'Failed to initialize emulator');
      } finally {
        setIsLoading(false);
      }
    };

    init();

    // Cleanup on unmount
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      saveSyncManager?.destroy();
      inputManager?.destroy();
      audioEngine.destroy();
      emulatorCore.destroy();
    };
  }, []); // Only run once on mount

  /**
   * Main emulation loop
   */
  const emulationLoop = useCallback((timestamp: number) => {
    if (!emulatorCore.isReady() || !renderEngine) {
      return;
    }

    // Run one frame
    emulatorCore.runFrame();

    // Render screens
    const topScreen = emulatorCore.getTopScreen();
    const bottomScreen = emulatorCore.getBottomScreen();
    if (topScreen || bottomScreen) {
      renderEngine.renderScreens(topScreen, bottomScreen);
    }

    // Process audio
    const audioBuffer = emulatorCore.getAudioBuffer();
    if (audioBuffer) {
      audioEngine.processAudio(audioBuffer);
    }

    // Update FPS counter
    frameCountRef.current++;
    if (timestamp - fpsUpdateTimeRef.current >= 1000) {
      const currentFps = Math.round(
        (frameCountRef.current * 1000) / (timestamp - fpsUpdateTimeRef.current)
      );
      setFps(currentFps);
      frameCountRef.current = 0;
      fpsUpdateTimeRef.current = timestamp;
    }

    animationFrameRef.current = requestAnimationFrame(emulationLoop);
  }, [emulatorCore, renderEngine, audioEngine]);

  /**
   * Start emulation
   */
  const startEmulation = useCallback(() => {
    if (!emulatorCore.isReady()) {
      return;
    }

    setIsRunning(true);
    setIsPaused(false);
    emulatorCore.resume();

    fpsUpdateTimeRef.current = performance.now();
    frameCountRef.current = 0;

    animationFrameRef.current = requestAnimationFrame(emulationLoop);
  }, [emulatorCore, emulationLoop]);

  /**
   * Handle ROM upload
   */
  const handleROMUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsLoading(true);
      setError(null);

      const fileSize = file.size;
      const fileSizeMB = (fileSize / 1024 / 1024).toFixed(2);

      console.log(`📂 Loading ROM: ${file.name} (${fileSizeMB} MB)`);
      toast.loading('Loading ROM...', { id: 'rom-load' });

      // Calculate ROM hash for save system
      const romBuffer = await file.arrayBuffer();
      const hash = await calculateROMHash(romBuffer);

      console.log(`🔐 ROM hash: ${hash.substring(0, 16)}...`);

      // Load ROM into emulator
      console.log('📥 Loading ROM into melonDS...');
      await emulatorCore.loadROM(romBuffer);

      // Store ROM info
      const romName = file.name.replace(/\.(nds|ds)$/i, '');
      setRomInfo({
        hash,
        name: romName,
        size: fileSize,
      });

      // Initialize save sync manager
      const syncManager = new SaveSyncManager(emulatorCore, {
        romHash: hash,
        romName,
        saveSlot: 0,
        autoSaveInterval: 5 * 60 * 1000, // 5 minutes
        enableCloudSync: true,
      });

      await syncManager.initialize();
      setSaveSyncManager(syncManager);

      // Start auto-save
      syncManager.start();

      toast.dismiss('rom-load');
      toast.success('ROM loaded successfully!');
      console.log('✅ ROM loaded successfully');

      // Start emulation
      renderEngine?.drawPlaceholder('Press START to begin');
      setTimeout(() => startEmulation(), 500);

    } catch (err) {
      console.error('❌ ROM loading failed:', err);
      toast.dismiss('rom-load');
      toast.error(err instanceof Error ? err.message : 'Failed to load ROM');
      setError(err instanceof Error ? err.message : 'Failed to load ROM');
    } finally {
      setIsLoading(false);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  /**
   * Toggle pause
   */
  const handleTogglePause = () => {
    if (isPaused) {
      emulatorCore.resume();
      animationFrameRef.current = requestAnimationFrame(emulationLoop);
      setIsPaused(false);
      toast.success('Resumed');
    } else {
      emulatorCore.pause();
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      setIsPaused(true);
      toast.success('Paused');
    }
  };

  /**
   * Manual save to cloud
   */
  const handleSaveToCloud = async () => {
    if (!saveSyncManager) {
      toast.error('Save system not initialized');
      return;
    }

    try {
      toast.loading('Saving to cloud...', { id: 'manual-save' });
      await saveSyncManager.saveToCloud();
      setLastSaveTime(new Date());
      toast.dismiss('manual-save');
      toast.success('Saved to cloud successfully!');
    } catch (error) {
      toast.dismiss('manual-save');
      toast.error('Failed to save to cloud');
      console.error('Save failed:', error);
    }
  };

  /**
   * Reset emulator
   */
  const handleReset = () => {
    if (window.confirm('Reset the emulator? This will restart the current game.')) {
      emulatorCore.reset();
      toast.success('Emulator reset');
    }
  };

  /**
   * Open key bindings settings
   */
  const handleOpenKeyBindings = () => {
    // Pause emulation while configuring keys
    if (isRunning && !isPaused) {
      handleTogglePause();
    }
    setShowKeyBindings(true);
  };

  /**
   * Close key bindings settings
   */
  const handleCloseKeyBindings = () => {
    setShowKeyBindings(false);
    // Reload key mapping in input manager
    if (inputManager) {
      const newMapping = loadKeyMapping();
      inputManager.setKeyMapping(newMapping);
      toast.success('Key bindings updated');
    }
  };

  /**
   * Go back to dashboard
   */
  const handleBackToDashboard = () => {
    if (isRunning) {
      if (window.confirm('Exit the emulator? Your progress will be auto-saved.')) {
        // Save before exiting
        if (saveSyncManager) {
          saveSyncManager.saveToCloud().catch(console.error);
        }
        navigate('/dashboard');
      }
    } else {
      navigate('/dashboard');
    }
  };

  return (
    <div className="emulator-page">
      <div className="emulator-container">
        <div className="emulator-header">
          <h1>🎮 Nintendo DS Emulator</h1>
          <button
            onClick={handleBackToDashboard}
            className="back-button"
            aria-label="Return to dashboard"
          >
            ← Back to Dashboard
          </button>
        </div>

        {error && (
          <div className="error-message">
            <strong>Error:</strong> {error}
            <button onClick={() => setError(null)} aria-label="Close error message">✕</button>
          </div>
        )}

        <div className="emulator-content">
          {/* melonDS dual-screen display */}
          <div className="screens-container">
            <div className="screen-wrapper">
              <canvas ref={topCanvasRef} className="ds-screen top-screen" />
              <span className="screen-label">Top Screen</span>
            </div>

            <div className="screen-wrapper">
              <canvas ref={bottomCanvasRef} className="ds-screen bottom-screen" />
              <span className="screen-label">Touch Screen</span>
            </div>
          </div>

          {/* ROM Info */}
          {romInfo && (
            <div className="rom-info-panel">
              <h3>📂 Current Game</h3>
              <div className="rom-details">
                <p><strong>Name:</strong> {romInfo.name}</p>
                <p><strong>Size:</strong> {(romInfo.size / 1024 / 1024).toFixed(2)} MB</p>
                <p><strong>Hash:</strong> {romInfo.hash.substring(0, 16)}...</p>
              </div>
            </div>
          )}

          {/* Controls Panel */}
          <div className="controls-panel">
            {!isRunning && (
              <div className="control-group">
                <label htmlFor="rom-upload" className="upload-button">
                  {isLoading ? '⏳ Loading ROM...' : '📂 Upload ROM File (.nds)'}
                </label>
                <input
                  ref={fileInputRef}
                  id="rom-upload"
                  type="file"
                  accept=".nds,.ds"
                  onChange={handleROMUpload}
                  disabled={isLoading}
                  aria-label="Upload Nintendo DS ROM file"
                  style={{ display: 'none' }}
                />
              </div>
            )}

            {isRunning && (
              <>
                <div className="control-buttons">
                  <button
                    onClick={handleTogglePause}
                    className="control-button"
                    aria-label={isPaused ? 'Resume emulation' : 'Pause emulation'}
                  >
                    {isPaused ? '▶️ Resume' : '⏸️ Pause'}
                  </button>

                  <button
                    onClick={handleSaveToCloud}
                    className="control-button control-button-save"
                    aria-label="Save to cloud"
                    title="Manually save your progress to the cloud"
                  >
                    ☁️ Save to Cloud
                  </button>

                  <button
                    onClick={handleOpenKeyBindings}
                    className="control-button"
                    aria-label="Configure keyboard controls"
                  >
                    ⌨️ Key Bindings
                  </button>

                  <button
                    onClick={handleReset}
                    className="control-button"
                    aria-label="Reset emulator and restart game"
                  >
                    🔄 Reset
                  </button>

                  <label htmlFor="rom-change" className="control-button">
                    📂 Load Different ROM
                  </label>
                  <input
                    id="rom-change"
                    type="file"
                    accept=".nds,.ds"
                    onChange={handleROMUpload}
                    disabled={isLoading}
                    aria-label="Load a different ROM file"
                    style={{ display: 'none' }}
                  />
                </div>

                <div className="status-info">
                  <div className="fps-counter">FPS: {fps}</div>
                  {lastSaveTime && (
                    <div className="last-save-time">
                      Last saved: {lastSaveTime.toLocaleTimeString()}
                    </div>
                  )}
                  <div className="auto-save-status">
                    ✓ Auto-save enabled (every 5 minutes)
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Keyboard Guide */}
        <div className="keyboard-guide">
          <div className="keyboard-guide-header">
            <h3>⌨️ Keyboard Controls</h3>
            <button
              onClick={handleOpenKeyBindings}
              className="btn btn-small btn-secondary"
              disabled={!inputManager}
            >
              Customize
            </button>
          </div>
          <div className="key-mappings">
            <div className="key-group">
              <strong>Movement:</strong>
              <span>↑↓←→: D-Pad</span>
            </div>
            <div className="key-group">
              <strong>Buttons:</strong>
              <span>Z: A Button</span>
              <span>X: B Button</span>
              <span>A: Y Button</span>
              <span>S: X Button</span>
            </div>
            <div className="key-group">
              <strong>Shoulders:</strong>
              <span>Q: L Button</span>
              <span>W: R Button</span>
            </div>
            <div className="key-group">
              <strong>System:</strong>
              <span>Enter: Start</span>
              <span>Shift: Select</span>
            </div>
          </div>
          <p className="keyboard-note">
            💡 <strong>Touch Screen:</strong> Click on the bottom screen to use touch controls
          </p>
          <p className="keyboard-note">
            ⚙️ <strong>Tip:</strong> Click "Customize" to change keyboard controls to your preference
          </p>
        </div>

        {/* Legal Notice */}
        <div className="legal-notice">
          <h4>⚠️ Legal Notice</h4>
          <p>
            You must legally own the games you play. This emulator is for personal use only.
            ROMs of games you do not own are illegal.
          </p>
          <p>
            <strong>Free Homebrew Games:</strong>{' '}
            <a
              href="https://www.gamebrew.org/wiki/Category:Nintendo_DS_homebrew_games"
              target="_blank"
              rel="noopener noreferrer"
            >
              GameBrew.org
            </a>
          </p>
        </div>

        {/* Technical Info */}
        <div className="tech-info">
          <p>
            <strong>Emulator Core:</strong> melonDS WASM
          </p>
          <p>
            <strong>Features:</strong> Custom key bindings, Cloud save sync, Auto-save
          </p>
          <p>
            <strong>Status:</strong>{' '}
            {isLoading && '⏳ Loading...'}
            {!isLoading && !isRunning && '✓ Ready'}
            {isRunning && isPaused && '⏸️ Paused'}
            {isRunning && !isPaused && '▶️ Running'}
          </p>
        </div>
      </div>

      {/* Key Bindings Settings Modal */}
      <KeyBindingsSettings
        isOpen={showKeyBindings}
        onClose={handleCloseKeyBindings}
      />
    </div>
  );
}
